import type { SupportRequestCategory } from "Components/Support/SupportRequestCategory";
import {
  VITE_BUILD_NUMBER,
  VITE_VERSION,
} from "Infrastructure/Utils/BuildUtils";
import { useMutation } from "@tanstack/react-query";
import {
  type ApiProblemDetails,
  apiPost,
  type FetchResponse,
  getApiUrl,
  postSupportRequestsPath,
  type ProblemDetails,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { getReportableUrl } from "Infrastructure/Utils/SupportUtils";
import i18next from "i18next";

/**
 * The rate limiter's 429 response has no body, so at runtime this case arrives as
 * `{ status: 429, data: null }` — branch on `status`, never on `data`.
 */
export type CreateSupportRequestError =
  | FetchResponse<ApiProblemDetails, 400 | 503>
  | FetchResponse<ProblemDetails, 429>;

/**
 * The request goes to our own API, which forwards it to Mo.one Brain and opens a ticket there.
 * Through the backend, not straight from the browser — Brain authenticates with a shared secret,
 * and in a frontend bundle that would be readable by anyone.
 *
 * Only an anonymous sender submits a name and e-mail. For a signed-in user the server takes them
 * from the token and ignores the body fields, so there is no point filling them in.
 */
export type CreateSupportRequestPayload = {
  category: SupportRequestCategory;
  subject: string;
  description: string;
  attachment: File | null;
  reporterName: string | null;
  reporterEmail: string | null;
};

const buildFormData = (payload: CreateSupportRequestPayload) => {
  const formData = new FormData();

  formData.append("Category", payload.category);
  formData.append("Subject", payload.subject);
  formData.append("Description", payload.description);

  if (payload.reporterName) {
    formData.append("Name", payload.reporterName);
  }

  if (payload.reporterEmail) {
    formData.append("Email", payload.reporterEmail);
  }

  if (payload.attachment) {
    formData.append("Attachment", payload.attachment);
  }

  formData.append("AppVersion", VITE_VERSION ?? "");
  formData.append("BuildNumber", VITE_BUILD_NUMBER ?? "");
  formData.append("Language", i18next.language);
  formData.append("Url", getReportableUrl());

  return formData;
};

type Props = {
  onSuccess?: () => void;
  onError?: (error: CreateSupportRequestError) => void;
};

export const useCreateSupportRequestMutation = ({
  onSuccess,
  onError,
}: Props = {}) =>
  useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      async (payload: CreateSupportRequestPayload) =>
        apiPost(
          `${getApiUrl()}${postSupportRequestsPath()}`,
          buildFormData(payload),
          undefined,
        ),
    ),
    onSuccess,
    onError,
  });
